/**
 * WooCommerce Integration Service
 *
 * ALL WooCommerce access goes through the backend proxy (/webhook/woo/* + /jobs/available).
 * The direct WooCommerce REST fallback was REMOVED: it required shipping the store's
 * consumer_key/secret in the web bundle, where anyone could read it and pull customer PII
 * (orders contain names, addresses, phones). The backend holds those credentials now.
 */
import { api } from './api';
import { mapToTrainingCategory, getTrainingCategoryLabel } from '../utils/categoryMapping';

/** Haversine distance in miles between two lat/lng points */
function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3959; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Estimate helpers needed based on category, product title, stairs, and location.
 * Used only to fill in a value when the backend job has none set.
 */
function estimateHelpersNeeded(
  trainingCategory: string,
  weightLbs: number = 0,
  productTitle: string = '',
  deliveryNotes: string = '',
  deliveryAddress: string = '',
): number {
  const three = ['hot-tub', 'swim-spa', 'pool-table', 'outdoor'];
  const two = [
    'fitness-equipment', 'treadmill', 'tonal', 'sauna', 'rowing-machine',
    'gym-equipment', 'furniture', 'appliance', 'golf-simulator', 'cold-plunge',
    'peloton-bike', 'massage-chair', 'reformer', 'elliptical', 'cable-machine',
    'squat-rack', 'stair-stepper', 'home-gym',
  ];

  const titleLower = productTitle.toLowerCase();
  const allNotes = `${deliveryNotes} ${deliveryAddress}`;
  const hasStairs = /stairs|staircase|flight|steps|second floor|third floor|2nd floor|3rd floor|upstairs|walk.?up/i.test(allNotes);
  const isTreadPlus = /tread\s*\+|tread\s*plus/i.test(titleLower);
  const isTread = !isTreadPlus && /tread/i.test(titleLower) && trainingCategory === 'treadmill';

  let needed = 1;

  // Tonal is always exactly 2 people, no exceptions
  if (trainingCategory === 'tonal') {
    needed = 2;
  } else if (isTreadPlus) {
    needed = hasStairs ? 4 : 3;
  } else if (isTread) {
    needed = hasStairs ? 3 : 2;
  } else if (three.includes(trainingCategory)) {
    needed = hasStairs ? 4 : 3;
  } else if (weightLbs > 200 || two.includes(trainingCategory)) {
    needed = hasStairs ? 3 : 2;
  } else {
    needed = hasStairs ? 2 : 1;
  }

  // Hot tubs / swim spas ALWAYS need at least 3 people total (1 driver + 2 helpers).
  if (/hot ?tub|swim ?spa|\bspa\b|jacuzzi/i.test(titleLower) && needed < 3) {
    needed = hasStairs ? 4 : 3;
  }

  return needed;
}

export const wooService = {
  async getOrders(status?: string) {
    try {
      const params = status ? { status } : {};
      const { data } = await api.get('/webhook/woo/orders', { params });
      return data;
    } catch {
      return [];
    }
  },

  async getAvailableJobs() {
    // Sync WooCommerce orders into backend before fetching (fire-and-forget).
    try { await api.post('/webhook/woo/sync-orders').catch(() => {}); } catch {}

    // Fetch from backend (knows which jobs are accepted vs available). The backend is the
    // single source — if it's unreachable we return [] rather than exposing WC credentials.
    try {
      const { data } = await api.get('/jobs/available');
      if (!Array.isArray(data)) return [];
      if (data.length === 0) return [];
      return data.map((job: any) => {
        const trainingCategory = mapToTrainingCategory(job.categorySlug || 'general');
        const cleanLabel = getTrainingCategoryLabel(trainingCategory);
        const distMiles = (job.pickupLat && job.deliveryLat)
          ? Math.round(haversineDistance(job.pickupLat, job.pickupLng, job.deliveryLat, job.deliveryLng))
          : 0;
        // Generate default delivery windows if backend has none
        const windows = (job.deliveryWindows && job.deliveryWindows.length > 0)
          ? job.deliveryWindows
          : [
              { id: `w-${job.id}-1`, startTime: new Date(Date.now() + 86400000).toISOString(), endTime: new Date(Date.now() + 86400000 + 14400000).toISOString() },
              { id: `w-${job.id}-2`, startTime: new Date(Date.now() + 172800000).toISOString(), endTime: new Date(Date.now() + 172800000 + 14400000).toISOString() },
            ];
        return {
          ...job,
          productTitle: job.productTitle || cleanLabel,
          productImages: job.productImages || [],
          categorySlug: trainingCategory,
          _distanceMiles: distMiles,
          // Respect an explicit backend value (including 0 = solo). Only estimate when none set.
          helpersNeeded: (job.helpersNeeded ?? null) !== null
            ? job.helpersNeeded
            : estimateHelpersNeeded(trainingCategory, 0, job.productTitle || '', job.deliveryNotes || '', job.deliveryAddress || ''),
          payoutAmount: job.payoutAmount ?? 0,
          deliveryWindows: windows,
        };
      });
    } catch (e: any) {
      console.warn('[Jobs] Backend available jobs failed:', e?.message);
      return [];
    }
  },

  async getCategories() {
    try {
      const { data } = await api.get('/webhook/woo/categories');
      return data;
    } catch {
      return [];
    }
  },

  async syncOrders() {
    try {
      const { data } = await api.post('/webhook/woo/sync-orders');
      return data;
    } catch {
      return { synced: 0, orders: [] };
    }
  },
};
